import { DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useTolgee } from '@tolgee/react';
import { Dropdown } from 'antd';
import mixpanel from 'mixpanel-browser';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import theme from '../assets/theme';
import useAccessBadge from '../hooks/useAccessBadge';
import { useBreakPoint } from '../hooks/useBreakPoint';
import {
  getBrowserLanguage,
  resolveLocaleFromServerSelection,
  storePreferredLocale,
} from '../hooks/useLocale';
import { localeOptions } from '../providers/tolgee';
import backendService from '../services/backendService';
import { zIndexes } from './zIndexes';

type GroupedOption =
  | { label: string; value: string }
  | { label: string; options: { label: string; value: string }[] };

const languageName = (displayName: string) => displayName.split(' (')[0];

const hiddenLocaleToVisibleLocale: Record<string, string> = {
  'en-US-TX': 'en-US',
  'en-US-CA': 'en-US',
};

const hiddenLocales = new Set(Object.keys(hiddenLocaleToVisibleLocale));

export const normalizeLocaleForSelector = (locale: string): string =>
  hiddenLocaleToVisibleLocale[locale] || locale;

const visibleLocaleOptions = localeOptions.filter(([value]) => !hiddenLocales.has(value));
export const localeOptionsForSelector = visibleLocaleOptions;

const groupedOptions: GroupedOption[] = Object.values(
  visibleLocaleOptions.reduce((acc, [value, displayName]) => {
    const key = value.slice(0, 2);
    if (!acc[key]) {
      acc[key] = { label: displayName, value };
    } else if ('options' in acc[key]) {
      (acc[key] as { options: { label: string; value: string }[] }).options.push({
        label: displayName,
        value,
      });
    } else {
      acc[key] = {
        label: languageName(displayName),
        options: [
          acc[key] as { label: string; value: string },
          { label: displayName, value },
        ],
      };
    }
    return acc;
  }, {} as Record<string, { label: string; value: string } | { label: string; options: { label: string; value: string }[] }>),
);

const SelectorTrigger = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: ${({ $compact }) => ($compact ? '4px 8px' : '6px 12px')};
  background: transparent;
  border: ${({ $compact }) => ($compact ? 'none' : `1px solid #d9d9d9`)};
  border-radius: 5px;
  gap: 8px;
  justify-content: center;
  min-width: ${({ $compact }) => ($compact ? '44px' : '0')};
  font-size: 13px;
  font-weight: 400;
  font-family: 'Nunito', sans-serif;
  white-space: nowrap;

  .flag {
    font-size: 14px;
  }

  .chevron {
    font-size: 10px;
    color: ${theme.colors.black};
    transition: transform 0.2s ease;
  }

  .chevron.rotated {
    transform: rotate(180deg);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const DropdownOverlay = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #d9d9d9;
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
`;

const GroupLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 12px;
  color: #999;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
`;

const DropdownItem = styled.div<{ $active?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  background-color: ${({ $active }) => ($active ? '#f5f5f5' : 'transparent')};

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const LangSelector: React.FC<{
  hideMobileVersion?: boolean;
  onSelect?: (locale: string) => void;
}> = ({ hideMobileVersion = false, onSelect }) => {
  const tolgee = useTolgee();
  const isMobile = !useBreakPoint(550);
  const { accessBadge, isFetching, refetchAccessBadge } = useAccessBadge();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: ({ locale }: { locale: string }) =>
      backendService.instance.users.editProfile({
        preferredLocale: locale,
      }),

    onSuccess: (result, { locale }) => {
      refetchAccessBadge();
      storePreferredLocale(result.preferredLocale ?? locale);
    },
  });

  const onChange = useCallback(
    async (locale: string) => {
      const resolvedLocale = await resolveLocaleFromServerSelection(locale);
      if (accessBadge?.user?.id) {
        mutate({ locale: resolvedLocale }, { onSuccess: () => onSelect?.(locale) });
        storePreferredLocale(resolvedLocale);
        tolgee.changeLanguage(resolvedLocale);
      } else {
        onSelect?.(locale);
        storePreferredLocale(resolvedLocale);
        tolgee.changeLanguage(resolvedLocale);
      }
      mixpanel.track('locale changed', {
        selectedLocale: locale,
        resolvedLocale,
        detectedLocale: getBrowserLanguage(),
      });
    },
    [accessBadge, mutate, tolgee, onSelect],
  );

  const currentLanguage = tolgee.getLanguage();
  const selectorLocale = currentLanguage
    ? normalizeLocaleForSelector(currentLanguage)
    : undefined;
  const currentLanguageDisplay = visibleLocaleOptions.find(
    ([value]) => value === selectorLocale,
  )?.[1];
  const compact = isMobile && !hideMobileVersion;

  const overlay = (
    <DropdownOverlay>
      {groupedOptions.map(option => {
        if ('options' in option) {
          return (
            <div key={option.label}>
              <GroupLabel>{option.label}</GroupLabel>
              {option.options.map(sub => (
                <DropdownItem
                  key={sub.value}
                  $active={sub.value === selectorLocale}
                  onClick={() => {
                    onChange(sub.value);
                    setIsDropdownOpen(false);
                  }}
                >
                  {sub.label}
                </DropdownItem>
              ))}
            </div>
          );
        }
        return (
          <DropdownItem
            key={option.value}
            $active={option.value === selectorLocale}
            onClick={() => {
              onChange(option.value);
              setIsDropdownOpen(false);
            }}
          >
            {option.label}
          </DropdownItem>
        );
      })}
    </DropdownOverlay>
  );

  return (
    <Dropdown
      overlay={overlay}
      placement="bottomRight"
      trigger={['click']}
      disabled={!currentLanguage || isMutating || isFetching}
      onVisibleChange={setIsDropdownOpen}
      overlayStyle={{ zIndex: zIndexes('ANTD_SELECT_DROPDOWN') }}
    >
      <SelectorTrigger $compact={compact}>
        <GlobalOutlined className="flag" />
        {!compact && <span>{currentLanguageDisplay}</span>}
        <DownOutlined className={`chevron ${isDropdownOpen ? 'rotated' : ''}`} />
      </SelectorTrigger>
    </Dropdown>
  );
};
